/* eslint-disable no-undef */
import { Form, Formik, useFormikContext } from 'formik'
import { t } from 'i18next'
import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import {
  useCreatePartnerMutation,
  useGetAllAgentsQuery,
  useGetAllCountriesQuery,
  useGetAllCurrenciesQuery,
  useGetAllPartnerGroupsQuery,
  useGetPartnerVATIDConfigQuery,
} from '../../../api/services/partnersApi'
import { toastApiValidationErrors } from '../../../utils/toastApiValidationErrors'
import SelectInput from '../../inputs/SelectInput'
import TextInput from '../../inputs/TextInput'
import Spinner from '../../other/Spinner'
import { Button } from '../../root/Button'
import { validatePartnerValues } from './FormikValidationPartnerModal'

const AddPartnerModal = ({ showModal, setShowModal }) => {
  const company = useSelector((state) => state.company.company)

  // taxId is ID of company in ERP applicaton
  const company_tax_id = useMemo(() => company?.taxId, [company])
  const pin = useMemo(() => company?.pin, [company])
  const ERP_API = useMemo(() => company?.ip, [company])

  const formData = {
    taxId: company_tax_id,
    pin,
    ip: ERP_API,
  }

  const { data: allCountries } = useGetAllCountriesQuery({ formData })
  const { data: allCurrencies } = useGetAllCurrenciesQuery({ formData })
  const { data: agents } = useGetAllAgentsQuery({ formData })
  const { data: allGroups } = useGetAllPartnerGroupsQuery({ formData })
  const { data: VATIDConfig } = useGetPartnerVATIDConfigQuery({ formData })

  const [triggerCreatePartner, { isLoading }] = useCreatePartnerMutation()

  const initialValues = {
    name: '', // *
    branch_office: 0, // *
    branch_office_id: '', // if branch_office *
    address_1: '', // *
    city: '', // *
    state: '', // *
    phone_1: '',
    mobile: '',
    website: '',
    email: '',
    limit: 0,
    limit_num: '0',
    deadline: '',
    id: '', // *
    obligee: 'Y',
    vat: '',
    responsible_person: '',
    currency_id: '', // *
    delivery_time: '',
    country_id: 0,
    country: '',
    zip_code: '', // *
    currency_name: '',
    responsible_person_id: null,
    partner_group_id: null,
  }

  const handleSubmit = (values) => {
    const createDataBody = {
      ...values,
      ...formData,
    }
    //console.log(values)

    triggerCreatePartner({ formData: createDataBody })
      .unwrap()
      .then(() => {
        toast.success('Partner uspješno kreiran')
        setShowModal(false)
      })
      .catch((err) => {
        toastApiValidationErrors(err?.data?.message, t('error'))
        console.error(err)
      })
  }

  const taxRegion = [
    {
      value: 'FBiH',
      label: t('fbih'),
    },
    {
      value: 'RS',
      label: t('rs'),
    },
    {
      value: 'BD',
      label: t('bd'),
    },
  ]

  return (
    <>
      {showModal && (
        <div
          onClick={() => setShowModal(false)}
          className="fixed inset-0 bg-black/20 z-[20] flex items-center justify-center"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-[40vw] p-8 bg-white z-[21] relative rounded-xl max-h-[80vh] overflow-y-auto"
          >
            <div onClick={() => setShowModal(false)} className="absolute top-8 right-8 text-lg cursor-pointer">
              X
            </div>
            {/* TITLE FOR THIS MODAL */}
            <div className="text-lg ">{t('open_modal')}</div>
            <div className="bg-black/40 w-full h-[1px] mt-1" />

            {/* FORMIK FOR ADDING PARTNER*/}
            <div className="mt-3 z-[22] relative">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={validatePartnerValues(VATIDConfig)}
              >
                {({ values, setFieldValue }) => {
                  return (
                    <Form className="flex flex-col justify-between gap-2 z-[22]">
                      <TextInput name={'name'} placeholder={t('name')} label={`${t('name')}*`} />
                      <SelectInput
                        label={`${t('branch_office')}*`}
                        options={[
                          { label: t('yes'), value: 1 },
                          { label: t('no'), value: 0 },
                        ]}
                        name={'branch_office'}
                        placeholder={t('select')}
                      />
                      {values.branch_office === 1 && (
                        <TextInput
                          name={'branch_office_id'}
                          placeholder={t('branch_office_id')}
                          label={`${t('branch_office_id')}*`}
                        />
                      )}
                      <TextInput name={'address_1'} placeholder={t('address')} label={`${t('address')}*`} />
                      <TextInput name={'city'} placeholder={t('city')} label={`${t('city')}*`} />

                      <SelectInput
                        label={`${t('country')}*`}
                        options={allCountries}
                        name="country"
                        placeholder={t('select')}
                        onChange={(option) => {
                          setFieldValue('country', option.label)
                          setFieldValue('state', option.value)
                          setFieldValue('country_id', option.id)
                        }}
                        //value={'test'}
                      />

                      {values.state === 'BA' && (
                        <SelectInput
                          label={`${t('entity')}*`}
                          name="entity"
                          placeholder={t('entity')}
                          options={taxRegion.map((item) => ({
                            value: item.value,
                            label: item.label,
                          }))}
                        />
                      )}

                      <TextInput name={'phone_1'} placeholder={t('phone')} label={t('phone')} />
                      <TextInput name={'mobile'} placeholder={t('mobile')} label={t('mobile')} />
                      <TextInput name={'website'} placeholder={t('website')} label={t('website')} />
                      <TextInput name={'email'} placeholder={'Email'} label={'Email'} />
                      <TextInput name={'zip_code'} placeholder={t('zip_code')} label={`${t('zip_code')}*`} />
                      <SelectInput
                        label={t('limit_for_orders')}
                        options={[
                          { label: t('yes'), value: 1 },
                          { label: t('no'), value: 0 },
                        ]}
                        name={'limit'}
                        placeholder={t('select')}
                      />
                      {values.limit === 1 && (
                        <TextInput
                          name={'limit_num'}
                          placeholder={t('limit_for_orders')}
                          label={t('limit_for_orders')}
                          defaultValue={'0'}
                        />
                      )}
                      <TextInput name={'deadline'} placeholder={t('deadline')} label={t('deadline')} />
                      <SelectInput
                        label={t('partner_groups')}
                        options={allGroups}
                        name={'partner_group'}
                        placeholder={t('select')}
                        onChange={(option) => {
                          setFieldValue('partner_group', option.label)
                          setFieldValue('partner_group_id', option.value)
                        }}
                      />
                      <TextInput
                        name={'id'}
                        placeholder={
                          VATIDConfig?.partner_id_required
                            ? `${VATIDConfig.partner_id_digit_limit} ${t('digits')}`
                            : 'ID'
                        }
                        label={'ID'}
                      />
                      <TextInput
                        name={'vat'}
                        placeholder={
                          VATIDConfig?.partner_tax_required
                            ? `${VATIDConfig.partner_tax_digit_limit} ${t('digits')}`
                            : t('vat')
                        }
                        label={t('vat')}
                      />
                      <SelectInput
                        label={t('agent')}
                        options={agents}
                        name={'responsible_person'}
                        placeholder={t('select')}
                        onChange={(option) => {
                          setFieldValue('responsible_person', option.label)
                          setFieldValue('responsible_person_id', option.value)
                        }}
                      />
                      <SelectInput
                        label={`${t('currency')}*`}
                        options={allCurrencies}
                        name={'currency_name'}
                        placeholder={t('select')}
                        onChange={(option) => {
                          setFieldValue('currency_name', option.label)
                          setFieldValue('currency_id', option.value)
                        }}
                      />
                      <TextInput name={'delivery_time'} placeholder={t('delivery_time')} label={t('delivery_time')} />
                      <div className="mt-5">
                        {isLoading ? (
                          <div className="w-full flex justify-center items-center">
                            <Spinner />
                          </div>
                        ) : (
                          <Button type="submit">{t('submit')}</Button>
                        )}
                      </div>
                    </Form>
                  )
                }}
              </Formik>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AddPartnerModal
